/*
  Retaillage et réencodage des photos, côté client, AVANT tout envoi.

  ╔══════════════════════════════════════════════════════════════════════╗
  ║  POURQUOI CE MODULE EXISTE                                           ║
  ║                                                                      ║
  ║  Un appareil photo de téléphone écrit automatiquement les            ║
  ║  coordonnées GPS dans le bloc EXIF du JPEG. Envoyer une photo brute  ║
  ║  reviendrait donc à transmettre la position exacte d'un nid — très   ║
  ║  précisément ce que tout le reste du projet s'interdit.              ║
  ║                                                                      ║
  ║  Le réencodage par canvas RECONSTRUIT le JPEG à partir des pixels.   ║
  ║  Il ne peut structurellement pas reporter le segment APP1/EXIF.      ║
  ║  Aucune métadonnée ne survit à l'opération.                          ║
  ║                                                                      ║
  ║  Le test ne fait pas confiance à ce raisonnement : il inspecte le    ║
  ║  blob sortant octet par octet.                                       ║
  ║  cf. tests/navigateur/photo-exif.test.ts                             ║
  ╚══════════════════════════════════════════════════════════════════════╝
*/

/** Plus grand côté après retaillage. */
export const COTE_MAX = 1280
export const QUALITE = 0.8

export function dimensionsCibles(
  largeur: number,
  hauteur: number,
  coteMax = COTE_MAX,
): { largeur: number; hauteur: number } {
  const plusGrand = Math.max(largeur, hauteur)
  if (plusGrand <= coteMax) return { largeur, hauteur }
  const facteur = coteMax / plusGrand
  return {
    largeur: Math.max(1, Math.round(largeur * facteur)),
    hauteur: Math.max(1, Math.round(hauteur * facteur)),
  }
}

async function decoder(source: Blob): Promise<{
  dessiner: (ctx: CanvasRenderingContext2D, l: number, h: number) => void
  largeur: number
  hauteur: number
  liberer: () => void
}> {
  if (typeof createImageBitmap === 'function') {
    // `from-image` applique l'orientation EXIF au moment du décodage : sans ça,
    // une photo prise en portrait ressortirait couchée une fois l'EXIF perdu.
    const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' })
    return {
      dessiner: (ctx, l, h) => ctx.drawImage(bitmap, 0, 0, l, h),
      largeur: bitmap.width,
      hauteur: bitmap.height,
      liberer: () => bitmap.close(),
    }
  }

  // Repli pour les WebView anciens.
  const url = URL.createObjectURL(source)
  const image = new Image()
  await new Promise<void>((resoudre, rejeter) => {
    image.onload = () => resoudre()
    image.onerror = () => rejeter(new Error('Image illisible'))
    image.src = url
  })
  return {
    dessiner: (ctx, l, h) => ctx.drawImage(image, 0, 0, l, h),
    largeur: image.naturalWidth,
    hauteur: image.naturalHeight,
    liberer: () => URL.revokeObjectURL(url),
  }
}

/**
 * Retaille à `coteMax` px de côté maximum et réencode en JPEG.
 *
 * Effet de bord recherché : la suppression totale des métadonnées EXIF,
 * dont le GPS.
 */
export async function redimensionner(
  source: Blob,
  coteMax = COTE_MAX,
  qualite = QUALITE,
): Promise<Blob> {
  const image = await decoder(source)
  try {
    const { largeur, hauteur } = dimensionsCibles(image.largeur, image.hauteur, coteMax)

    const canvas = document.createElement('canvas')
    canvas.width = largeur
    canvas.height = hauteur

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D indisponible')
    image.dessiner(ctx, largeur, hauteur)

    const blob = await new Promise<Blob | null>((resoudre) =>
      canvas.toBlob(resoudre, 'image/jpeg', qualite),
    )
    if (!blob) throw new Error("Le réencodage JPEG n'a rien produit")
    return blob
  } finally {
    image.liberer()
  }
}
